import { SupportRequestDraftDtoIn } from '@/models/in/SupportRequestDraftDtoIn';
import { defaultSupportRequestFormValues, SupportRequestFormData } from '../schemas/supportRequestFormSchema';

/**
 * Turns a stored draft back into values the new-request form can be reset with.
 *
 * The draft is written by another client - the Nedarim personal area - in this form's own
 * field names, so most of the work is refusing anything that is not one of them. It is the
 * only place in the app that reads payloadJson.
 *
 * ⚠️ Everything here tolerates a partial, stale or malformed draft and never throws. A draft
 * is by definition an unfinished form, it may have been written by an older build, and it
 * arrives as free-form JSON. The worst outcome allowed is an empty form; losing the whole
 * request screen to a bad draft is not.
 */

/**
 * ⚠️ THIS LIST IS A CONTRACT WITH ANOTHER CODEBASE, AND THE `satisfies` BELOW IS WHAT
 * ENFORCES IT.
 *
 * A draft is written by the Nedarim personal area (hatzdaka-nedarim,
 * js/pages/support-request.js) in this form's own field names. Renaming a field here without
 * renaming it there does not fail anywhere at runtime: the draft still saves, and it opens on
 * this screen with that field quietly missing - so a coordinator retypes what they already
 * typed, and nobody sees an error.
 *
 * The `satisfies readonly (keyof SupportRequestFormData)[]` makes that rename break THIS
 * build instead, which is the point. If a compile error brought you here, the other side
 * needs the same rename; that repo has a test (test/draft-field-names.test.js) holding the
 * mirror of this list.
 *
 * ⚠️ documents and additionalDocuments are absent on purpose, and must stay absent.
 *
 * They hold File objects, which cannot survive JSON - and they are the entire reason drafts
 * exist: the coordinator is here precisely because they could not attach them. Letting a
 * draft carry those keys would overwrite files the user has just picked on this screen with
 * empty arrays from the draft.
 */
const RESTORABLE_KEYS = [
  'supported',
  'goalWithLimits',
  'supportRecipient',
  'childId',
  'weddingDate',
  'documentDate',
  'kollelName',
  'studyTimes',
  'kollelAddress',
  'studyMaterial',
  'roshKollelName',
  'multiRequestTotalCount',
  'paymentStartTiming',
  'paymentStartMonth',
  'transferDayOfMonth',
  'paymentInfoId',
  'supplierPaymentInfoId',
  'amount',
  'userComments',
  'exceedsBalanceComment',
  'isAlreadyPaid',
  'selectedSupplierId',
  'supplierName',
  'supplierTaxId',
  'invoiceNumber',
  'bankCode',
  'bankAccount',
  'bankBranch',
  'payerName',
  'payerIsraeliId',
] as const satisfies readonly (keyof SupportRequestFormData)[];

/**
 * ⚠️ Not restored even though they are ordinary fields: these four are conclusions the form
 * reaches by comparing the amount against balances and limits it fetches live. A draft saved
 * last week may have been over the limit then and inside it now, or the reverse. Restoring
 * the stale answer would put a request past a limit that no longer applies, or waive an
 * approval that now does. The form recomputes them, so they are simply left at their
 * defaults: exceedsAvailableBalance, exceedsEligibleAmount, exceptionsCommitteeAcknowledged,
 * isDuplicateInvoiceNumber.
 */

type RestorableKey = (typeof RESTORABLE_KEYS)[number];

const isPlainObject = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null && !Array.isArray(value);

/**
 * Reads the draft into form values, or returns null when there is nothing usable in it.
 * Null means "open the form as if there were no draft" - never an error screen.
 */
export function mapDraftToFormData(draft: SupportRequestDraftDtoIn | null | undefined): Partial<SupportRequestFormData> | null {
  if (!draft?.payloadJson) return null;

  let parsed: unknown;
  try {
    parsed = JSON.parse(draft.payloadJson);
  } catch {
    // A draft we cannot read is a draft the coordinator has to fill in again. Nothing else
    // on the page should suffer for it.
    return null;
  }

  if (!isPlainObject(parsed)) return null;

  const values: Partial<SupportRequestFormData> = {};

  RESTORABLE_KEYS.forEach((key: RestorableKey) => {
    const value = parsed[key];

    // undefined means the writer never filled this in; null is a real, restorable value for
    // the many fields whose default is null.
    if (value === undefined) return;

    /*
     * ⚠️ Checked against the default's type rather than trusted. A number field that arrives
     * as the string the input held - "1200" instead of 1200 - would pass zod's type check
     * nowhere and surface as a validation error the coordinator cannot explain, on a field
     * they can see is filled in.
     */
    const fallback = defaultSupportRequestFormValues[key];
    if (fallback !== null && value !== null && typeof value !== typeof fallback) return;

    (values as Record<string, unknown>)[key] = value;
  });

  return Object.keys(values).length > 0 ? values : null;
}

/**
 * The supported carried by the draft, for the screens that need it before the form is built -
 * the request dialog skips its own "choose a supported" step when a draft already names one.
 */
export function readDraftSupported(draft: SupportRequestDraftDtoIn | null | undefined): SupportRequestFormData['supported'] {
  const values = mapDraftToFormData(draft);
  return values?.supported ?? null;
}
